import React, { useContext } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Link, Typography } from '@mui/material';
import { AppContext } from '../../../../../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useUrlParams from 'hooks/useUrlParams';
import { TemplateDetailsProvider } from '../../_shared/Details/context';
import { useTemplateDetailsContext } from '../../_shared/Details/context';
import TemplateDetails from './tabs/TemplateDetails';
import EditTemplate from './tabs/EditTemplate';
import TemplateOutline from './tabs/TemplateOutline';
import VersionHistory from './tabs/VersionHistory';
import ChildTemplates from './tabs/ChildTemplates';

export type TemplateTab = {
  label: React.ReactNode;
  key: string;
  component: React.ReactNode;
  count?: number;
};

export type TemplateUrlParams = {
  tab: string;
};

const defaultParams: TemplateUrlParams = {
  tab: 'details',
};

const LexipolDetails: React.FC = () => {
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const { template, parentTemplate, isChildTemplate } = useTemplateDetailsContext();
  const [params, setParams] = useUrlParams(defaultParams);
  const navigate = useNavigate();

  const tabs: { [key: string]: TemplateTab } = React.useMemo(() => {
    const allTabs: { [key: string]: TemplateTab } = {
      details: {
        label: t('performanceReporting.templates.details.tabs.details'),
        key: 'details',
        component: <TemplateDetails />,
      },
      outline: {
        label: t('performanceReporting.templates.details.tabs.outline'),
        key: 'outline',
        component: <TemplateOutline />,
      },
    };
    // Always show ChildTemplates tab for Lexipol (only hide for agency templates)
    if (!isChildTemplate) {
      allTabs.childTemplates = {
        label: t('performanceReporting.templates.details.tabs.childTemplates'),
        key: 'childTemplates',
        component: <ChildTemplates />,
      };
    }
    // Always show Version History for Lexipol
    allTabs.versionHistory = {
      label: t('performanceReporting.templates.details.tabs.versionHistory'),
      key: 'versionHistory',
      component: <VersionHistory />,
    };

    return allTabs;
  }, [isChildTemplate, t]);

  const updateParams = React.useCallback(
    (updatedParams: TemplateUrlParams) => {
      setParams((prev: TemplateUrlParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  // Get the valid tab value - fallback to 'details' if current tab doesn't exist
  const activeTab = React.useMemo(() => {
    return tabs[params.tab] ? params.tab : 'details';
  }, [tabs, params.tab]);

  const headerTitle = template?.name || '';

  const breadcrumbItems = React.useMemo(() => {
    const items = [
      <Link
        key='b1'
        href='/admin/performance-reporting/templates'
      >
        {t('performanceReporting.templates.list.title')}
      </Link>,
    ];

    // Add parent template link if this is a child template
    if (isChildTemplate && template?.parentTemplateId) {
      const parentName = parentTemplate?.name || 'Parent Template';
      items.push(
        <Link
          key='b2'
          href={`/admin/performance-reporting/templates/details/${template.parentTemplateId}`}
        >
          {parentName}
        </Link>
      );
    }

    // Add current template name
    items.push(<Typography key='b3'>{template?.name || ''}</Typography>);

    return items;
  }, [isChildTemplate, template?.parentTemplateId, template?.name, parentTemplate?.name, t]);

  return (
    <CommonLayout
      headerProps={{
        title: headerTitle,
        breadcrumbItems,
        actions: null,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: activeTab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[activeTab]?.component}
    </CommonLayout>
  );
};

const LexipolDetailsWrapper: React.FC = () => {
  return (
    <TemplateDetailsProvider>
      <Routes>
        <Route
          path='edit'
          element={<EditTemplate />}
        />
        <Route
          path='*'
          element={<LexipolDetails />}
        />
      </Routes>
    </TemplateDetailsProvider>
  );
};

export default LexipolDetailsWrapper;

